<template>
  <div class="world-map">
    <div ref="mapContainer" style="height: 100%; width: 100%" />
    <div class="world-map__overlay">Say "hello from_____" to get on the map!</div>
  </div>
</template>

<script>
import maplibregl from 'maplibre-gl/dist/maplibre-gl-csp'
import 'maplibre-gl/dist/maplibre-gl.css'

maplibregl.workerUrl = process.env.BASE_URL + 'maplibre-gl-csp-worker.js'

const STYLE_URL = 'https://tiles.openfreemap.org/styles/positron'
const LAND_COLOR = 'rgb(59, 184, 131)'
const OCEAN_COLOR = 'rgb(36, 75, 151)'

export default {
  name: 'WorldMap',
  props: {
    comments: {
      type: Array,
      default: () => []
    },
    activeId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      map: null,
      markers: []
    }
  },
  watch: {
    comments: {
      handler() {
        this.updateMarkers()
      },
      deep: true
    },
    activeId(newVal, oldVal) {
      this.updateMarkers()
      if (newVal) {
        const comment = this.comments.find(c => c.id === newVal)
        if (comment) {
          this.flyToComment(comment)
        }
      } else if (oldVal && !newVal) {
        this.resetView()
      }
    }
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    this.markers.forEach(m => m.remove())
    this.markers = []
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    initMap() {
      fetch(STYLE_URL)
        .then(res => res.json())
        .then(style => {
          // 自定义配色：陆地绿色、海洋蓝色
          style.layers.forEach(layer => {
            if (layer.type === 'background') {
              layer.paint = layer.paint || {}
              layer.paint['background-color'] = LAND_COLOR
            }
            if (layer['source-layer'] === 'water' && layer.type === 'fill') {
              layer.paint = layer.paint || {}
              layer.paint['fill-color'] = OCEAN_COLOR
            }
          })

          this.map = new maplibregl.Map({
            container: this.$refs.mapContainer,
            style,
            center: [0, 20],
            zoom: 1,
            attributionControl: false
          })
          this.map.scrollZoom.setWheelZoomRate(1 / 200)

          this.map.on('load', () => {
            // 自适应容器，尽量放大并显示完整全球
            this.map.fitBounds([[-170, -55], [190, 80]], { padding: 0, duration: 0 })
            this.updateMarkers()
          })
        })
    },
    updateMarkers() {
      this.markers.forEach(m => m.remove())
      this.markers = []
      if (!this.map) return

      this.comments.forEach(item => {
        const isActive = this.activeId === item.id
        const size = isActive ? 64 : 48
        const border = isActive ? 3 : 2

        const el = document.createElement('div')
        el.className = 'avatar-marker'

        const inner = document.createElement('div')
        inner.className = 'avatar-marker__inner' + (isActive ? ' avatar-marker__inner--active' : '')
        inner.style.cssText = `width:${size}px;height:${size}px;border-width:${border}px;`

        const img = document.createElement('img')
        img.src = item.avatar
        img.alt = item.username

        inner.appendChild(img)
        el.appendChild(inner)
        el.addEventListener('click', () => this.$emit('select', item))

        const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([item.lng, item.lat])
          .addTo(this.map)

        this.markers.push(marker)
      })
    },
    flyToComment(comment) {
      if (!this.map) return
      this.map.flyTo({
        center: [comment.lng, comment.lat],
        zoom: 10,
        duration: 1500
      })
    },
    resetView() {
      if (!this.map) return
      this.map.fitBounds([[-170, -55], [190, 80]], { duration: 1200 })
    }
  }
}
</script>

<style lang="scss" scoped>
.world-map {
  height: 100%;
  width: 100%;
  position: relative;

  &__overlay {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 1000;
    pointer-events: none;
    background: rgba(0, 0, 0, 0.65);
    color: $white;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    backdrop-filter: blur(4px);
    letter-spacing: 0.3px;
  }

  ::v-deep .avatar-marker {
    cursor: pointer;

    &__inner {
      border-radius: 50%;
      border: 2px solid $white;
      overflow: hidden;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
      background: #ccc;
      transition: all 0.3s ease;

      &--active {
        border-color: $primary;
        box-shadow: 0 2px 12px rgba($primary, 0.5);
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }
  }
}
</style>
